<template>
  <div class="automation-configure-task-settings-component" v-bind:class="{loaded, loading}">
    <div v-if="!loaded">
      <a-form-model-item label="Template">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Type" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Beknopte omschrijving" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Notitie toevoegen" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 115px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Toewijzen aan gebruiker(s)" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Toewijzen aan afdeling(en)" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Labels">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Datum" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Categorie" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
      <a-form-model-item label="Prioriteit" :required="true">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; max-width: 365px; height: 32px;"></Skeleton>
      </a-form-model-item>
    </div>
    <div v-else>
      <a-form-model-item label="Template">
        <a-select class="task-input-template mt4" style="width: 100%;"
                  placeholder="Selecteer een taak template"
                  v-model="taskSelectedTemplate"
                  @select="handleSelectTemplate"
                  :disabled="loading"
                  allowClear>
          <a-select-option v-for="(template, key) in taskTemplates" :key="key" :value="key">
            {{ template.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item label="Type" :required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('task_type')"
                         :help="getValidationMessage('task_type')">
        <a-select class="task-input-type mt4" style="width: 100%;" :disabled="loading" v-bind:value="type"
                  placeholder="Selecteer het type taak"
                  @change="onTypeChange">
          <a-select-option v-for="(value, key) in TaskTypeEnum" :key="key" :value="value">
            {{ translateTaskType(value) }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item label="Beknopte omschrijving" :required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('task_title')"
                         :help="getValidationMessage('task_title')">
        <a-input class="task-input-content mt4" style="width: 100%;" :disabled="loading"
                 v-bind:value="taskTitle"
                 placeholder="Omschrijf de taak"
                 @change="onContentChange"/>
      </a-form-model-item>

      <a-form-model-item label="Notitie toevoegen" :required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('task_description')"
                         :help="getValidationMessage('task_description')">
        <a-textarea class="task-input-description mt4" style="width: 100%;" :disabled="loading"
                    @change="onDescriptionChange"
                    v-bind:value="taskDescription" placeholder="Omschrijf de taak"
                    :rows="5"/>
      </a-form-model-item>

      <a-form-model-item label="Toewijzen aan gebruiker(s)" :required="doesntHaveSelectedDepartments"
                         :has-feedback="true"
                         :validate-status="hasValidationErrors('task_assigned_users')"
                         :help="getValidationMessage('task_assigned_users')">
        <users-select class="task-input-users mt4" style="width: 100%;" :disabled="loading"
                      v-bind:value="assignedUsers"
                      @change="onUsersChange"/>
      </a-form-model-item>

      <a-form-model-item label="Toewijzen aan afdeling(en)" :required="doesntHaveSelectedUsers" :has-feedback="true"
                         :validate-status="hasValidationErrors('task_assigned_user_departments')"
                         :help="getValidationMessage('task_assigned_user_departments')">
        <user-departments-select class="task-input-departments mt4" :disabled="loading"
                                 v-bind:value="assignedUserDepartments"
                                 @change="onUserDepartmentsChange"/>
      </a-form-model-item>

      <a-alert show-icon style="margin-top: 1.5em;"
               :type="doesntHaveSelectedDepartments && doesntHaveSelectedUsers ? 'warning' : 'success'">
        <template v-slot:message>
          Selecteer tenminste één; gebruiker of afdeling.
        </template>
      </a-alert>

      <a-form-model-item label="Labels">
        <product-labels-select class="task-input-labels mt4" style="width: 100%;" :disabled="loading"
                               v-model="assignedProductLabels"
                               product-category="task"/>
      </a-form-model-item>

      <a-form-model-item label="Datum" title="Uitvoeren voor" :required="true" :has-feedback="true"
                         :validate-status="hasValidationErrors('task_create_in_days')"
                         :help="getValidationMessage('task_create_in_days')">
        <a-button-group class="margin-bottom" style="line-height: initial !important;">
          <dd-button v-for="suggestion in suggestions"
                     :key="suggestion.description"
                     @click="setCreateInDays(suggestion.amount)"
                     :title="'Uitvoeren binnen ' + suggestion.amount + ' dagen.'"
                     :type="suggestion.amount === createInDays ? 'primary' : 'outline'"
                     class="mr-sm"
          >
            {{ suggestion.description }}
          </dd-button>
        </a-button-group>
      </a-form-model-item>

      <a-form-model-item label="Categorie"
                         :required="true"
                         :has-feedback="true"
                         :validate-status="hasValidationErrors('task_category')"
                         :help="getValidationMessage('task_category')">
        <category-select v-model="category"
                         :disabled="loading"
                         @change="onCategoryChange"
                         class="task-input-category mt4"/>
      </a-form-model-item>

      <a-form-model-item label="Prioriteit"
                         :required="true"
                         :has-feedback="true"
                         :validate-status="hasValidationErrors('task_priority')"
                         :help="getValidationMessage('task_priority')">
        <a-radio-group name="task-input-priority"
                       class="task-input-priority mt4"
                       :disabled="loading"
                       button-style="solid"
                       @change="onPriorityChange"
                       v-bind:value="priority"
                       :defaultValue="TaskPriorityEnum.NORMAL">
          <a-radio-button v-for="(value, key) in TaskPriorityEnum" :key="key" :value="value">
            {{ translateTaskPriority(value) }}
          </a-radio-button>
        </a-radio-group>
      </a-form-model-item>

    </div>
  </div>
</template>

<script>
import DynamicDateEnum from "~/enums/DynamicDateEnum";
import TaskPriorityEnum from "~/enums/TaskPriorityEnum";
import TaskStatusEnum from "~/enums/TaskStatusEnum";
import TaskTypeEnum from "~/enums/TaskTypeEnum";
import { Skeleton } from "vue-loading-skeleton";
import store from "../store";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ConfigureTaskSettingsComponent",
  components: {
    Skeleton
  },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      DynamicDateEnum,
      TaskPriorityEnum,
      TaskStatusEnum,
      TaskTypeEnum,
      loaded: false,
      loading: false,
      suggestions: [
        {
          description: "vandaag",
          amount: DynamicDateEnum.TODAY,
        },
        {
          description: "1 dag",
          amount: DynamicDateEnum.TOMORROW,
        },
        {
          description: "1 week",
          amount: DynamicDateEnum.NEXT_WEEK,
        },
        {
          description: "2 weken",
          amount: DynamicDateEnum.NEXT_TWO_WEEKS,
        },
        {
          description: "1 maand",
          amount: DynamicDateEnum.NEXT_MONTH,
        },
        {
          description: "1 jaar",
          amount: DynamicDateEnum.NEXT_YEAR,
        },
        {
          description: "3 jaren",
          amount: DynamicDateEnum.NEXT_THREE_YEARS,
        },
        {
          description: "5 jaren",
          amount: DynamicDateEnum.NEXT_FIVE_YEARS,
        }
      ],
      defaultType: TaskTypeEnum.GENERAL_TASK
    };
  },
  mounted() {
    const STORE_KEY = "$_automation";
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store);
    }

    this.defaultType = this.translateTaskType(this.type);

    Promise.all([
      this.fetchCategories(),
      this.fetchUsers(),
      this.fetchUserDepartments(),
      this.fetchTaskTemplates(),
      this.fetchMe(),
    ])
        .then()
        .catch()
        .finally(() => {
          this.loading = false;
          this.loaded = true;
        });
  },
  computed: {
    ...mapGetters('$_automation', [
      'taskTemplates',
      'categories',
      'users',
      'userDepartments',
      'me',
    ]),
    ...mapGetters('$_form_page', [
      'errorsObject',
    ]),
    assignedUsers: {
      get() {
        return this.value.task_assigned_users || [];
      }
    },
    assignedUserDepartments: {
      get() {
        return this.value.task_assigned_user_departments || [];
      }
    },
    doesntHaveSelectedDepartments() {
      return this.assignedUserDepartments === null
          || this.assignedUserDepartments === undefined
          || this.assignedUserDepartments.length === 0;
    },
    doesntHaveSelectedUsers() {
      return this.assignedUsers === null
          || this.assignedUsers === undefined
          || this.assignedUsers.length === 0;
    },
    assignedProductLabels: {
      get() {
        return this.value.task_assigned_product_labels || [];
      },
      set(value) {
        this.$emit("input", Object.assign({}, this.value, { task_assigned_product_labels: value }));
      }
    },
    taskTitle: {
      get() {
        return this.value.task_title;
      }
    },
    taskDescription: {
      get() {
        return this.value.task_description;
      }
    },
    createInDays: {
      get() {
        return this.value.task_create_in_days;
      },
    },
    category: {
      get() {
        return this.value.task_category || [];
      },
      set(value) {
        this.$emit("input", Object.assign({}, this.value, { task_category: value }));
      }
    },
    priority: {
      get() {
        return this.value.task_priority;
      }
    },
    type: {
      get() {
        return this.value.task_type || [];
      }
    },
    taskSelectedTemplate: {
      get() {
        return (this.value.task_selected_template === undefined || this.value.task_selected_template === null)
            ? [] : this.value.task_selected_template;
      },
      set(value) {
        this.$emit("input", Object.assign({}, this.value, { task_selected_template: value }));
      }
    },
  },
  methods: {
    ...mapActions('$_automation', {
      fetchCategories: "fetchCategories",
      fetchUsers: "fetchUsers",
      fetchUserDepartments: "fetchUserDepartments",
      fetchTaskTemplates: "fetchTaskTemplates",
      fetchMe: "fetchMe",
    }),
    setCreateInDays(days) {
      this.$emit("input", Object.assign({}, this.value, { task_create_in_days: days }));
      this.clearErrorsByName('task_create_in_days');
    },
    getTemplateByIndex(index) {
      return this.taskTemplates[index];
    },
    onTypeChange(value) {
      this.$emit("input", Object.assign({}, this.value, { task_type: value }));
      this.clearErrorsByName('task_type');
    },
    onContentChange(event) {
      this.$emit("input", Object.assign({}, this.value, { task_title: event.target.value }));
      this.clearErrorsByName('task_title');
    },
    onDescriptionChange(event) {
      this.$emit("input", Object.assign({}, this.value, { task_description: event.target.value }));
      this.clearErrorsByName('task_description');
    },
    onUsersChange(value) {
      this.$emit("input", Object.assign({}, this.value, { task_assigned_users: value }));
      this.clearSignalAssignedToValidationErrors();
    },
    onUserDepartmentsChange(value) {
      this.$emit("input", Object.assign({}, this.value, { task_assigned_user_departments: value }));
      this.clearSignalAssignedToValidationErrors();
    },
    onCategoryChange(value) {
      this.clearErrorsByName('task_category');
    },
    onPriorityChange(event) {
      this.$emit("input", Object.assign({}, this.value, { task_priority: event.target.value }));
      this.clearErrorsByName('task_priority');
    },
    handleSelectTemplate(value) {
      this.$confirm({
        content: "Reeds ingevulde velden kunnen worden overschreven. Wil je doorgaan?",
        okText: "Ja",
        cancelText: "Annuleren",
        onOk: () => {
          const template = this.getTemplateByIndex(value);
          let automation = Object.assign({}, this.value);

          for (let attribute in template) {
            if (["users", "product_labels"].includes(attribute)) {
              automation[`task_assigned_${attribute}`] = template[attribute].data.map(item => item.id);
            } else if (attribute === "date") {
              automation["task_create_in_days"] = template[attribute];
            } else if (attribute === "departments") {
              automation[`task_assigned_user_${attribute}`] = template[attribute].data.map(item => item.id);
            } else if (attribute === "content") {
              automation["task_title"] = template[attribute];
            } else if (attribute === "category_id") {
              automation["task_category"] = template[attribute];
            } else {
              automation["task_" + attribute] = template[attribute];
            }
          }

          this.$emit("input", automation);
          this.clearTaskErrors();
        },
        onCancel: () => {
          this.$emit("input", Object.assign({}, this.value, { task_selected_template: null }));
        },
      });
    },
    translateTaskPriority(priority) {
      switch (priority) {
        case TaskPriorityEnum.CRITICAL:
          return "Kritiek";
        case TaskPriorityEnum.IMPORTANT:
          return "Belangrijk";
        case TaskPriorityEnum.NORMAL:
          return "Normaal";
        case TaskPriorityEnum.INSIGNIFICANT:
          return "Onbelangrijk";
      }
    },
    translateTaskType(type) {
      switch (type) {
        case TaskTypeEnum.CALL_BACK_REQUEST:
          return "Terugbelverzoek";
        case TaskTypeEnum.CHECK_TASK:
          return "Controle taak";
        case TaskTypeEnum.CORRESPOND_TASK:
          return "Correspondentie taak";
        case TaskTypeEnum.BIRTHDAY_TASK:
          return "Verjaardag";
        case TaskTypeEnum.GENERAL_TASK:
          return "Algemene taak";
        case TaskTypeEnum.MAINTENANCE_PRIVATE:
          return "Onderhoud particulier";
        case TaskTypeEnum.MAINTENANCE_BUSINESS:
          return "Onderhoud zakelijk";
      }
    },
    hasValidationErrors (key) {
      if (this.errorsObject !== undefined) {
        return Object.hasOwn(this.errorsObject, key) ? 'error' : null;
      }
    },
    getValidationMessage (key) {
      if (this.errorsObject !== undefined) {
        return Object.hasOwn(this.errorsObject, key) ? this.errorsObject[key][0] : null;
      }
    },
    clearErrorsByName (key){
      if ('$_form_page' in this.$store._modules.root._children) {
        this.$store.commit('$_form_page/CLEAR_ERRORS_BY_NAME', key);
      }
    },
    clearSignalAssignedToValidationErrors() {
      this.clearErrorsByName('task_assigned_user_departments');
      this.clearErrorsByName('task_assigned_users');
    },
    clearTaskErrors() {
      for (const taskItem of [
        'task_type',
        'task_title',
        'task_description',
        'task_assigned_users',
        'task_assigned_user_departments',
        'task_assigned_product_labels',
        'task_create_in_days',
        'task_category',
        'task_priority',
      ]) {
        this.clearErrorsByName(taskItem);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.automation-configure-task-settings-component {
  position: relative;

  .ant-select {
    width: 100% !important;
    display: block !important;
  }
}
</style>
